import React, {useCallback} from 'react';
import {Team} from '../../interfaces';
import {DataGrid, GridColDef, GridRowModel} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {Paper} from '@mui/material';
import {DateTime} from 'luxon';

interface Props {
  teams: Team[];
  height?: number;
}

const TeamsTable: React.FC<Props> = ({teams, height = 700}) => {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      filterable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      filterable: true,
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      filterable: true,
    },
    {
      field: 'teamSessionGroupsEnabled',
      headerName: 'Team Session Groups Enabled',
      width: 220,
      filterable: true,
    },
    {
      field: 'isPro',
      headerName: 'Pro',
      width: 90,
      filterable: true,
    },
    {
      field: 'membershipTrialEndDate',
      headerName: 'Membership End Date',
      width: 200,
      filterable: true,
      valueFormatter: params =>
        params.value
          ? DateTime.fromISO(params.value as string).toFormat('yyyy-MM-dd')
          : '',
    },
  ];

  const handleRowClick = useCallback(
    (row: GridRowModel) => {
      navigate(`/admin/teams/${row.id}`);
    },
    [navigate],
  );

  const getRowClassName = useCallback(({row}: {row: Team}) => {
    if (!row.membershipTrialEndDate) {
      return 'membership-active';
    }
    return DateTime.fromISO(row.membershipTrialEndDate) > DateTime.now()
      ? 'membership-trial'
      : 'membership-expired';
  }, []);

  return (
    <Paper sx={{height}}>
      <DataGrid
        rows={teams ?? []}
        columns={columns}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        sx={{
          '& .membership-active': {
            backgroundColor: 'rgba(76, 175, 80, 0.2)',
            '&:hover': {backgroundColor: 'rgba(76, 175, 80, 0.3)'},
          },
          '& .membership-trial': {
            backgroundColor: 'rgba(255, 235, 59, 0.2)',
            '&:hover': {backgroundColor: 'rgba(255, 235, 59, 0.3)'},
          },
          '& .membership-expired': {
            backgroundColor: 'rgba(244, 67, 54, 0.2)',
            '&:hover': {backgroundColor: 'rgba(244, 67, 54, 0.3)'},
          },
        }}
      />
    </Paper>
  );
};

export default React.memo(TeamsTable);
